// Cave84 — Dashboard · Bronze Nocturne direction
// Layout: Hero → Stats strip → Main grid (Drink Soon + Regions) → Cellars ribbon → Tasks
// All design values from design_handoff_cave84/directions/bronze.jsx

import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  useInventory,
  useGroupedInventory,
  useConsumedInventory,
  DEFAULT_FILTERS,
  canonicalRegion,
  aggregateDrinkSoonRows,
  drinkSoonUrgencyLabel,
  maturityBandFor,
  MATURITY_BAND_STATUSES,
  type MaturityBand,
} from '@/hooks/useInventory';
import { useCellars } from '@/hooks/useCellars';
import { usePriceRefreshQueue } from '@/hooks/useMarketPrices';
import { listPendingEntries } from '@/services/generationQueue';
import { countWinesWithoutAnalysis } from '@/services/wineAnalysis';
import { getLatestPricesForVintages } from '@/services/marketPrices';
import type { InventoryRow } from '@/hooks/useInventory';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useValuationBasis } from '@/hooks/useValuationBasis';
import { valuateBottle } from '@/utils/valuation';
import { Money } from '@/components/Money';
import { usePrivacy } from '@/hooks/usePrivacy';
import { useIsOwner } from '@/stores/auth.store';
import type { Cellar } from '@cave84/shared-types';

// ---------------------------------------------------------------------------
// Bronze Nocturne design tokens
// ---------------------------------------------------------------------------
const BN = {
  bg:          '#0E1116',
  bg2:         '#141821',
  bg3:         '#1B202B',
  surf:        '#181D26',
  hair:        '#232936',
  hair2:       '#2F3645',
  hair3:       '#404858',
  ink:         '#ECE6D4',
  ink2:        '#B3AC97',
  ink3:        '#9C9483',
  ink4:        '#908977',
  accent:      '#C49255',
  accentSoft:  'rgba(196,146,85,0.16)',
  accentDeep:  '#8E663A',
  pos:         '#8FB57C',
  neg:         '#C46F50',
  cool:        '#7E9A98',
};

// Region colors for stacked bars (per spec)
const REGION_COLORS: Record<string, string> = {
  Burgundy:  BN.accent,
  Bordeaux:  BN.neg,
  Rhône:     BN.accentDeep,
  Champagne: BN.cool,
  Italy:     BN.pos,
  Spain:     BN.accentDeep,
  Germany:   '#7A8B72',
  Hungary:   BN.accent,
  Alsace:    BN.accentDeep,
  Loire:     BN.cool,
  Portugal:  BN.neg,
  USA:       '#6A7A8A',
  Argentina: '#7A5A8A',
  Australia: BN.accentDeep,
  Other:     '#8C95A8',
};

// Maturity band order, labels, and colors for the WI-5 dashboard card
const MATURITY_ORDER: MaturityBand[] = ['too_young', 'in_window', 'at_peak', 'past_peak'];

const MATURITY_LABELS: Record<MaturityBand, string> = {
  too_young: 'Too Young',
  in_window: 'In Window',
  at_peak:   'At Peak',
  past_peak: 'Past Peak',
};

const MATURITY_COLORS: Record<MaturityBand, string> = {
  too_young: BN.cool,
  in_window: BN.pos,
  at_peak:   BN.accent,
  past_peak: BN.neg,
};

// Cellar dot colors (per spec)
function cellarDotColor(c: Cellar): string {
  if (c.is_home) return BN.accent;
  if (c.is_virtual) return '#9282A4';
  const n = c.name.toLowerCase();
  if (n.includes('cyprus'))  return BN.cool;
  if (n.includes('berlin'))  return '#8C95A8';
  if (n.includes('romania')) return BN.accentDeep;
  return BN.ink3;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const ALL_FILTERS  = { ...DEFAULT_FILTERS, availableAtHome: false };
const HOME_FILTERS = { ...DEFAULT_FILTERS, availableAtHome: true  };

function tsToDate(ts: unknown): Date | null {
  if (!ts) return null;
  try {
    if (typeof (ts as { toDate?: () => Date }).toDate === 'function')
      return (ts as { toDate: () => Date }).toDate();
    return new Date((ts as { seconds: number }).seconds * 1000);
  } catch { return null; }
}

function fmtRelative(ts: unknown): string {
  const d = tsToDate(ts);
  if (!d) return '--';
  const diff = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (diff === 0) return 'today';
  if (diff === 1) return 'yesterday';
  if (diff < 7)  return `${diff}d ago`;
  if (diff < 14) return '1w ago';
  if (diff < 32) return `${Math.floor(diff / 7)}w ago`;
  if (diff < 60) return '1mo ago';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function ytdCount(rows: InventoryRow[], field: keyof InventoryRow['bottle']): number {
  const year = new Date().getFullYear();
  return rows.filter(r => {
    const d = tsToDate(r.bottle[field]);
    return d && d.getFullYear() === year;
  }).length;
}

function toRomanIndex(i: number): string {
  const romans = ['i','ii','iii','iv','v','vi','vii','viii','ix','x'];
  return romans[i] ?? String(i + 1);
}

// ---------------------------------------------------------------------------
// Cost / Market basis toggle (WI-3) — shared across Regions + Cellars
// breakdowns so both stay in sync. Default is Market (see valuationBasis.store).
// ---------------------------------------------------------------------------
function BasisToggle() {
  const { basis, setBasis } = useValuationBasis();
  return (
    <div style={{ display: 'inline-flex', border: `1px solid ${BN.hair2}`, borderRadius: 100, overflow: 'hidden' }}>
      {(['market', 'cost'] as const).map(b => (
        <button
          key={b}
          type="button"
          onClick={() => setBasis(b)}
          style={{
            padding: '3px 10px',
            fontSize: 11,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            fontWeight: 500,
            border: 'none',
            cursor: 'pointer',
            background: basis === b ? BN.accentSoft : 'transparent',
            color: basis === b ? BN.accent : BN.ink3,
          }}
        >
          {b === 'market' ? 'Market' : 'Cost'}
        </button>
      ))}
    </div>
  );
}

function UnpricedNote({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span style={{ fontSize: 11.5, color: BN.ink4, fontStyle: 'italic', marginInlineStart: 6 }}>
      {count} unpriced
    </span>
  );
}

// ---------------------------------------------------------------------------
// Hero section (kicker + h1 + lead + big numbers)
// ---------------------------------------------------------------------------
function Hero() {
  const isMobile = useIsMobile();
  const isOwner = useIsOwner();
  const { hidden: pricesHidden } = usePrivacy();
  const { rows: allRows } = useInventory(ALL_FILTERS);
  const { rows: consumedRows } = useConsumedInventory();

  // Unique vintage IDs across all active bottles
  const vintageIds = useMemo(
    () => [...new Set(allRows.map(r => r.vintage.id))],
    [allRows]
  );

  const { data: pricesMap = new Map() } = useQuery({
    queryKey: ['marketPrices', 'latestBatch', vintageIds],
    queryFn: () => getLatestPricesForVintages(vintageIds),
    enabled: vintageIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const stats = useMemo(() => {
    const totalBottles = allRows.length;
    const costBasis    = allRows.reduce((s, r) => s + (r.bottle.cost_eur_at_purchase ?? 0), 0);
    const ytdAdded     = ytdCount(allRows, 'purchase_date');
    const ytdConsumed  = ytdCount(consumedRows, 'status_changed_date');

    let marketValue   = 0;
    let pricedBottles = 0;
    for (const r of allRows) {
      const mp = pricesMap.get(r.vintage.id);
      if (mp) { marketValue += mp.price; pricedBottles++; }
    }

    const gainLossEur = pricedBottles > 0 ? marketValue - costBasis : null;
    const gainLossPct = pricedBottles > 0 && costBasis > 0
      ? ((marketValue - costBasis) / costBasis) * 100
      : null;
    const marketCoverage = totalBottles > 0 ? Math.round((pricedBottles / totalBottles) * 100) : 0;

    return { totalBottles, costBasis, ytdAdded, ytdConsumed,
             marketValue: pricedBottles > 0 ? marketValue : null,
             gainLossEur, gainLossPct, pricedBottles, marketCoverage };
  }, [allRows, consumedRows, pricesMap]);

  return (
    <div style={{
      padding: isMobile ? '20px 16px' : '28px 32px',
      borderBottom: `1px solid ${BN.hair}`,
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : (isOwner ? '1fr 1px 1fr 1px 1fr' : '1fr'),
      gap: 0,
      alignItems: 'center',
    }}>
      {/* Stat 1 — Cellar Count. Guests only ever see this one — purchase
          cost and market value are Owner-only per the Guest access policy. */}
      <div style={{ padding: isMobile ? '0 0 20px' : (isOwner ? '0 32px 0 0' : 0), borderBottom: isMobile ? `1px solid ${BN.hair}` : 'none' }}>
        <div style={{
          fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase',
          color: BN.ink3, fontWeight: 500,
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        }}>
          <span>Cellar · count</span>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", color: BN.pos, letterSpacing: 0, textTransform: 'none' }}>
            +{stats.ytdAdded} YTD
          </span>
        </div>
        <div style={{
          fontFamily: "'Fraunces', serif",
          fontWeight: 250, fontSize: isMobile ? 36 : 44, lineHeight: 1,
          letterSpacing: '-0.025em', color: BN.ink, marginTop: 6,
        }}>
          {stats.totalBottles}
          <span style={{ fontSize: 13, color: BN.ink3, fontStyle: 'italic', marginLeft: 8, fontWeight: 300 }}>across cellars</span>
        </div>
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: BN.ink3, marginTop: 4 }}>
          {stats.ytdAdded} in · {stats.ytdConsumed} out this year
        </div>
      </div>

      {isOwner && !isMobile && <div style={{ width: 1, background: BN.hair, alignSelf: 'stretch' }} />}

      {/* Stat 2 — Cost Basis (Owner-only: purchase price) */}
      {isOwner && (
        <div style={{ padding: isMobile ? '20px 0' : '0 32px', borderBottom: isMobile ? `1px solid ${BN.hair}` : 'none' }}>
          <div style={{ fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase', color: BN.ink3, fontWeight: 500 }}>
            Cost Basis
          </div>
          <div style={{
            fontFamily: "'Fraunces', serif",
            fontWeight: 250, fontSize: isMobile ? 36 : 44, lineHeight: 1,
            letterSpacing: '-0.025em', color: BN.ink, marginTop: 6,
          }}>
            <Money value={stats.costBasis} currency="EUR" minWidthCh={7} />
          </div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: BN.ink3, marginTop: 4 }}>
            purchase basis
          </div>
        </div>
      )}

      {isOwner && !isMobile && <div style={{ width: 1, background: BN.hair, alignSelf: 'stretch' }} />}

      {/* Stat 3 — Market Value (Owner-only: cellar valuation summary) */}
      {isOwner && (
        <div style={{ padding: isMobile ? '20px 0 0' : '0 0 0 32px' }}>
          <div style={{
            fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase',
            color: BN.ink3, fontWeight: 500,
            display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
          }}>
            <span>Market Value</span>
            {stats.gainLossPct !== null && (
              <span
                aria-label={pricesHidden ? 'Hidden value' : undefined}
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  letterSpacing: 0, textTransform: 'none',
                  color: pricesHidden ? BN.ink3 : (stats.gainLossPct >= 0 ? BN.pos : BN.neg),
                }}>
                {pricesHidden ? '•••' : `${stats.gainLossPct >= 0 ? '+' : ''}${stats.gainLossPct.toFixed(1)}%`}
              </span>
            )}
          </div>
          <div style={{
            fontFamily: "'Fraunces', serif",
            fontWeight: 250, fontSize: isMobile ? 36 : 44, lineHeight: 1,
            letterSpacing: '-0.025em', color: BN.ink, marginTop: 6,
          }}>
            {stats.marketValue !== null ? (
              <Money value={stats.marketValue} currency="EUR" minWidthCh={7} />
            ) : (
              <span style={{ fontSize: 18, color: BN.ink4, fontStyle: 'italic', fontWeight: 300 }}>no prices yet</span>
            )}
          </div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: BN.ink3, marginTop: 4 }}>
            {stats.marketValue !== null ? (
              <>
                {stats.gainLossEur !== null && (
                  <Money
                    value={stats.gainLossEur}
                    currency="EUR"
                    sign
                    intent={stats.gainLossEur >= 0 ? 'positive' : 'negative'}
                    minWidthCh={7}
                    style={{ marginRight: 6 }}
                  />
                )}
                <span>{stats.marketCoverage}% bottles priced</span>
              </>
            ) : (
              <span>run price refresh to populate</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stats strip — 5 cellars, % distribution
// ---------------------------------------------------------------------------
function StatsStrip() {
  const isMobile = useIsMobile();
  const isOwner = useIsOwner();
  const { data: cellars = [] } = useCellars();
  const { rows } = useInventory(ALL_FILTERS);
  const { basis } = useValuationBasis();

  const { cellarStats, totalUnpriced } = useMemo(() => {
    const counts    = new Map<string, number>();
    const values    = new Map<string, number>();
    const unpriced  = new Map<string, number>();
    let totalUnpriced = 0;
    for (const r of rows) {
      counts.set(r.cellar.id, (counts.get(r.cellar.id) ?? 0) + 1);
      const cost = r.bottle.cost_eur_at_purchase;
      const { value } = basis === 'market'
        ? valuateBottle(r.marketPrice, cost)
        : valuateBottle(null, cost); // cost-only basis: no market fallback
      if (value !== null) {
        values.set(r.cellar.id, (values.get(r.cellar.id) ?? 0) + value);
      } else {
        unpriced.set(r.cellar.id, (unpriced.get(r.cellar.id) ?? 0) + 1);
        totalUnpriced++;
      }
    }
    const total = rows.length || 1;
    const cellarStats = cellars.slice(0, 5).map(c => ({
      cellar: c,
      count:  counts.get(c.id) ?? 0,
      value:  values.get(c.id) ?? 0,
      unpriced: unpriced.get(c.id) ?? 0,
      pct:    Math.round((( counts.get(c.id) ?? 0) / total) * 100),
      dotColor: cellarDotColor(c),
    }));
    return { cellarStats, totalUnpriced };
  }, [cellars, rows, basis]);

  if (!cellarStats.length) return null;

  return (
    <div style={{ margin: isMobile ? '0 16px 20px' : '0 32px 28px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <span style={{ fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase', color: BN.ink3, fontWeight: 500 }}>
          {isOwner ? (
            <>
              Cellars &middot; {basis === 'market' ? 'market value' : 'cost basis'}
              <UnpricedNote count={totalUnpriced} />
            </>
          ) : (
            'Cellars · bottle count'
          )}
        </span>
        {isOwner && <BasisToggle />}
      </div>
      <div style={{
        padding: '18px 0',
        borderTop: `1px solid ${BN.hair}`,
        borderBottom: `1px solid ${BN.hair}`,
        display: 'grid',
        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : `repeat(${cellarStats.length}, 1fr)`,
        gap: isMobile ? '16px 0' : 0,
      }}>
        {cellarStats.map((item, i) => (
          <div key={item.cellar.id} style={{
            padding: isMobile ? '0 12px' : (i === 0 ? '0 24px 0 0' : i === cellarStats.length - 1 ? '0 0 0 24px' : '0 24px'),
            borderRight: isMobile ? 'none' : (i < cellarStats.length - 1 ? `1px solid ${BN.hair}` : 'none'),
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}>
            <div style={{
              fontSize: 11,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: BN.ink3,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: item.dotColor, flexShrink: 0 }} />
                {item.cellar.name}
              </span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", letterSpacing: 0, fontSize: 11.5, color: BN.accent }}>
                {item.pct}%
              </span>
            </div>
            <div style={{
              fontFamily: "'Fraunces', serif",
              fontWeight: 300,
              fontSize: 26,
              letterSpacing: '-0.02em',
              lineHeight: 1.05,
              color: BN.ink,
            }}>
              {item.count}
            </div>
            {isOwner && (
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: BN.ink3 }}>
                <Money value={item.value} currency="EUR" minWidthCh={7} />
                <UnpricedNote count={item.unpriced} />
              </div>
            )}
            {/* micro-bar */}
            <div style={{ height: 1.5, background: BN.bg3, marginTop: 4 }}>
              <div style={{ height: '100%', background: item.dotColor, width: `${item.pct}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Drink Soon card
// ---------------------------------------------------------------------------
function DrinkSoon() {
  const { grouped } = useGroupedInventory(HOME_FILTERS);

  // Collapse cellar/format splits into one row per producer+cuvée+vintage,
  // then sort by urgency (soonest window close first).
  const soon = useMemo(() => {
    const declining = grouped.filter(r => r.drinkingStatus === 'declining');
    return aggregateDrinkSoonRows(declining)
      .sort((a, b) => a.drinkingWindow.to - b.drinkingWindow.to)
      .slice(0, 5);
  }, [grouped]);

  if (!soon.length) return (
    <p style={{ fontSize: 12, color: BN.ink4, padding: '24px 18px', textAlign: 'center' }}>
      No bottles approaching window
    </p>
  );

  return (
    <ul style={{ listStyle: 'none' }}>
      {soon.map((r, i) => {
        const urgency = drinkSoonUrgencyLabel(r.drinkingWindow);
        return (
          <li key={r.key} style={{ borderBottom: `1px solid ${BN.hair}` }}>
            <Link
              to={`/wines/${r.wine.id}?vintage=${r.vintage.year}`}
              style={{
                display: 'grid',
                gridTemplateColumns: '26px 1fr 70px 108px',
                gap: 12,
                padding: '12px 18px',
                textDecoration: 'none',
                alignItems: 'center',
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(200,178,135,0.04)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.background = 'transparent'; }}
            >
              <span style={{
                fontFamily: "'Fraunces', serif",
                fontStyle: 'italic',
                color: BN.accent,
                fontSize: 15,
                fontWeight: 300,
              }}>
                {toRomanIndex(i)}.
              </span>
              <div>
                <div style={{
                  fontFamily: "'Fraunces', serif",
                  fontSize: 15,
                  fontWeight: 400,
                  letterSpacing: '-0.005em',
                  color: BN.ink,
                }}>
                  {r.producer.name}
                </div>
                <div style={{ fontSize: 11, color: BN.ink3, marginTop: 2 }}>
                  {r.wine.cuvee_name}
                </div>
              </div>
              <span style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 12,
                color: BN.ink2,
                textAlign: 'right',
              }}>
                {r.vintage.year === 1001 ? 'NV' : r.vintage.year} · ×{r.qty}
              </span>
              <span style={{
                fontSize: 11,
                padding: '3px 8px',
                border: `1px solid ${BN.neg}`,
                borderRadius: 100,
                letterSpacing: '0.03em',
                textTransform: 'uppercase',
                color: BN.neg,
                textAlign: 'center',
                fontWeight: 500,
                whiteSpace: 'nowrap',
              }}>
                {urgency}
              </span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Regions card
// ---------------------------------------------------------------------------
function RegionsCard() {
  const isOwner = useIsOwner();
  const { rows } = useInventory(ALL_FILTERS);
  const { basis } = useValuationBasis();

  const { regionData, totalUnpriced } = useMemo(() => {
    const map = new Map<string, { count: number; value: number; unpriced: number }>();
    let totalUnpriced = 0;
    for (const r of rows) {
      const region = canonicalRegion(r.producer.region ?? '');
      const e = map.get(region) ?? { count: 0, value: 0, unpriced: 0 };
      e.count++;
      // Guests: never compute a monetary figure at all — cost_eur_at_purchase
      // must not leak, including via the market-basis cost fallback. See
      // StatsStrip (Cellars ribbon) for the same pattern.
      if (isOwner) {
        const cost = r.bottle.cost_eur_at_purchase;
        const { value } = basis === 'market'
          ? valuateBottle(r.marketPrice, cost)
          : valuateBottle(null, cost); // cost-only basis: no market fallback
        if (value !== null) {
          e.value += value;
        } else {
          e.unpriced++;
          totalUnpriced++;
        }
      }
      map.set(region, e);
    }
    const total = rows.length || 1;
    const regionData = [...map.entries()]
      .sort(([, a], [, b]) => b.count - a.count)
      .slice(0, 5)
      .map(([region, d]) => ({ region, ...d, pct: Math.round((d.count / total) * 100), color: REGION_COLORS[region] ?? BN.ink3 }));
    return { regionData, totalUnpriced };
  }, [rows, basis, isOwner]);

  if (!regionData.length) return <p style={{ fontSize: 12, color: BN.ink4, padding: '24px 18px' }}>No data</p>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 18px 0' }}>
        <span style={{ fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: BN.ink3 }}>
          {isOwner ? (
            <>
              {basis === 'market' ? 'market value' : 'cost basis'}
              <UnpricedNote count={totalUnpriced} />
            </>
          ) : (
            'bottle count'
          )}
        </span>
        {isOwner && <BasisToggle />}
      </div>
      <ul style={{ listStyle: 'none', padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 13 }}>
        {regionData.map(item => (
          <li key={item.region}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr auto auto',
              gap: 12,
              alignItems: 'baseline',
              marginBottom: 4,
            }}>
              <span style={{ fontFamily: "'Fraunces', serif", fontSize: 14, fontWeight: 350, color: BN.ink }}>{item.region}</span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11.5, color: BN.accent }}>{item.pct}%</span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: BN.ink3 }}>
                {isOwner ? (
                  <>
                    {item.count} · <Money value={item.value} currency="EUR" minWidthCh={7} />
                    <UnpricedNote count={item.unpriced} />
                  </>
                ) : (
                  item.count
                )}
              </span>
            </div>
            {/* 4px stacked bar */}
            <div style={{ height: 4, background: BN.bg3, display: 'flex', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${item.pct}%`, background: item.color }} />
              <div style={{ height: '100%', flex: 1, background: BN.ink4 }} />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Maturity distribution card (WI-5)
// One horizontal stacked bar, 4 segments (Too Young / In Window / At Peak /
// Past Peak), by bottle count. Click a segment → Inventory pre-filtered to
// the matching drinking-status band.
// ---------------------------------------------------------------------------
function MaturityCard() {
  const navigate = useNavigate();
  const { rows } = useInventory(ALL_FILTERS);

  const bandData = useMemo(() => {
    const counts = new Map<MaturityBand, number>();
    for (const r of rows) {
      const band = maturityBandFor(r.drinkingStatus);
      counts.set(band, (counts.get(band) ?? 0) + 1);
    }
    const total = rows.length || 1;
    return MATURITY_ORDER.map(band => {
      const count = counts.get(band) ?? 0;
      return { band, count, pct: Math.round((count / total) * 100) };
    });
  }, [rows]);

  if (!rows.length) {
    return <p style={{ fontSize: 12, color: BN.ink4, padding: '24px 18px', textAlign: 'center' }}>No data</p>;
  }

  function goToBand(band: MaturityBand) {
    navigate('/inventory', { state: { drinkingStatuses: MATURITY_BAND_STATUSES[band] } });
  }

  return (
    <div style={{ padding: '16px 18px 18px' }}>
      {/* single stacked bar, one segment per maturity band */}
      <div style={{ height: 10, borderRadius: 3, background: BN.bg3, display: 'flex', overflow: 'hidden' }}>
        {bandData.filter(d => d.count > 0).map(d => (
          <button
            key={d.band}
            type="button"
            onClick={() => goToBand(d.band)}
            title={`${MATURITY_LABELS[d.band]} — ${d.count} (${d.pct}%)`}
            style={{
              height: '100%',
              width: `${d.pct}%`,
              background: MATURITY_COLORS[d.band],
              border: 'none',
              padding: 0,
              cursor: 'pointer',
            }}
          />
        ))}
      </div>
      <ul style={{
        listStyle: 'none',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: '14px 16px',
        marginTop: 16,
      }}>
        {bandData.map((d, idx) => (
          <li
            key={d.band}
            style={{
              paddingInlineStart: idx > 0 ? 16 : 0,
              borderInlineStart: idx > 0 ? `1px solid ${BN.hair}` : 'none',
            }}
          >
            <button
              type="button"
              onClick={() => goToBand(d.band)}
              disabled={d.count === 0}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                background: 'transparent',
                border: 'none',
                padding: 0,
                textAlign: 'start',
                cursor: d.count > 0 ? 'pointer' : 'default',
              }}
            >
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: MATURITY_COLORS[d.band], flexShrink: 0 }} />
              <span style={{ fontSize: 12.5, color: BN.ink2, whiteSpace: 'nowrap' }}>{MATURITY_LABELS[d.band]}</span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11.5, color: BN.ink3, whiteSpace: 'nowrap' }}>
                {d.count} &middot; {d.pct}%
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tasks card
// ---------------------------------------------------------------------------
function TasksCard() {
  const { data: priceQueue = [] } = usePriceRefreshQueue();
  const { data: analysisQueue = [] } = useQuery({
    queryKey: ['queue', 'wine_analysis', 'pending'],
    queryFn: () => listPendingEntries('wine_analysis'),
    staleTime: 2 * 60 * 1000,
  });
  const { data: winesWithoutAnalysis = 0 } = useQuery({
    queryKey: ['wines', 'without_analysis'],
    queryFn: countWinesWithoutAnalysis,
    staleTime: 5 * 60 * 1000,
  });

  const pendingAnalyses = analysisQueue.filter(e => e.status === 'pending' || e.status === 'processing').length;
  const pendingPrices   = priceQueue.filter(e => e.status === 'pending' || e.status === 'processing').length;
  const pendingReview   = priceQueue.filter(e => e.status === 'needs_owner_review').length;

  const tasks = [
    winesWithoutAnalysis > 0 && {
      urgent: true,
      label: `${winesWithoutAnalysis} ${winesWithoutAnalysis === 1 ? 'wine' : 'wines'} missing analysis`,
      meta:  'No drinking window, score, or tasting notes — run: npx tsx apps/cli/run-analyses.ts',
      to:    '/settings',
    },
    pendingAnalyses > 0 && winesWithoutAnalysis === 0 && {
      urgent: false,
      label: `${pendingAnalyses} ${pendingAnalyses === 1 ? 'analysis' : 'analyses'} generating`,
      meta:  'Auto-generating via Cloud Function — check back in a few minutes',
      to:    '/settings',
    },
    pendingPrices > 0 && {
      urgent: false,
      label: `${pendingPrices} price ${pendingPrices === 1 ? 'refresh' : 'refreshes'} pending`,
      meta:  'Auto-processing in the background — check back in a few minutes',
      to:    '/prices/refresh',
    },
    pendingReview > 0 && {
      urgent: true,
      label: `${pendingReview} price ${pendingReview === 1 ? 'update' : 'updates'} need review`,
      meta:  'Accept or override suggested prices',
      to:    '/prices/review',
    },
  ].filter(Boolean) as Array<{ urgent: boolean; label: string; meta: string; to: string }>;

  if (!tasks.length) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px' }}>
        <span style={{ color: BN.pos }}>✓</span>
        <span style={{ fontSize: 13, color: BN.ink2 }}>All caught up</span>
      </div>
    );
  }

  return (
    <div>
      {tasks.map(t => (
        <Link
          key={t.to}
          to={t.to}
          style={{
            display: 'grid',
            gridTemplateColumns: '22px 1fr auto',
            gap: 12,
            padding: '12px 18px',
            borderBottom: `1px solid ${BN.hair}`,
            textDecoration: 'none',
            alignItems: 'center',
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(200,178,135,0.04)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.background = 'transparent'; }}
        >
          <div style={{
            width: 22,
            height: 22,
            borderRadius: 4,
            background: BN.bg3,
            border: `1px solid ${t.urgent ? BN.neg : BN.hair2}`,
            display: 'grid',
            placeItems: 'center',
            fontSize: 11,
            color: t.urgent ? BN.neg : BN.accent,
          }}>
            {t.urgent ? '!' : '○'}
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 500, color: BN.ink }}>{t.label}</div>
            <div style={{ fontSize: 11, color: BN.ink3, marginTop: 1 }}>{t.meta}</div>
          </div>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: BN.ink3 }}>→</span>
        </Link>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Just Arrived card
// ---------------------------------------------------------------------------
function JustArrived() {
  const { rows } = useInventory(HOME_FILTERS);

  const recent = useMemo(() => [...rows]
    .sort((a, b) => (tsToDate(b.bottle.purchase_date)?.getTime() ?? 0) - (tsToDate(a.bottle.purchase_date)?.getTime() ?? 0))
    .slice(0, 4), [rows]);

  if (!recent.length) return (
    <p style={{ fontSize: 12, color: BN.ink4, padding: '24px 18px', textAlign: 'center' }}>No recent arrivals</p>
  );

  return (
    <div>
      {recent.map(r => (
        <Link
          key={r.bottle.id}
          to={`/wines/${r.wine.id}?vintage=${r.vintage.year}`}
          style={{
            display: 'grid',
            gridTemplateColumns: '22px 1fr 80px',
            gap: 12,
            padding: '12px 18px',
            borderBottom: `1px solid ${BN.hair}`,
            textDecoration: 'none',
            alignItems: 'center',
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(200,178,135,0.04)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.background = 'transparent'; }}
        >
          <div style={{
            width: 22,
            height: 22,
            borderRadius: 4,
            background: BN.bg3,
            border: `1px solid ${BN.hair2}`,
            display: 'grid',
            placeItems: 'center',
            fontSize: 12,
            color: BN.accent,
          }}>
            ◊
          </div>
          <div>
            <div style={{ fontFamily: "'Fraunces', serif", fontSize: 14, fontWeight: 400, color: BN.ink, letterSpacing: '-0.005em' }}>
              {r.producer.name}
            </div>
            <div style={{ fontSize: 11, color: BN.ink3, marginTop: 2 }}>
              {r.wine.cuvee_name} · {r.vintage.year === 1001 ? 'NV' : r.vintage.year} · {r.cellar.name}
            </div>
          </div>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: BN.ink3, textAlign: 'right' }}>
            {fmtRelative(r.bottle.purchase_date)}
          </span>
        </Link>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card wrapper
// ---------------------------------------------------------------------------
function Card({ title, count, action, actionTo, children }: {
  title: string;
  count?: string;
  action?: string;
  actionTo?: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{
      background: BN.surf,
      border: `1px solid ${BN.hair}`,
      borderRadius: 4,
      overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        padding: '14px 18px',
        borderBottom: `1px solid ${BN.hair}`,
      }}>
        <h3 style={{
          fontFamily: "'Fraunces', serif",
          fontWeight: 350,
          fontSize: 16,
          letterSpacing: '-0.005em',
          color: BN.ink,
          margin: 0,
          display: 'flex',
          alignItems: 'baseline',
          gap: 10,
        }}>
          {title}
          {count && (
            <span style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 11,
              color: BN.ink3,
              fontWeight: 400,
            }}>
              {count}
            </span>
          )}
        </h3>
        {action && actionTo && (
          <Link to={actionTo} style={{
            fontSize: 11.5,
            color: BN.ink3,
            borderBottom: `1px solid ${BN.hair2}`,
            paddingBottom: 1,
            textDecoration: 'none',
          }}>
            {action} →
          </Link>
        )}
      </div>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function DashboardPage() {
  const isMobile = useIsMobile();
  return (
    <div style={{ minHeight: '100vh', background: BN.bg }}>
      <div style={{ position: 'relative', zIndex: 1, maxWidth: 1440, margin: '0 auto' }}>

        <Hero />
        <StatsStrip />

        {/* Maturity distribution — WI-5 */}
        <div style={{ padding: isMobile ? '0 16px 20px' : '0 32px 28px' }}>
          <Card title="the cellar, by readiness" count="4 bands" action="view all" actionTo="/inventory">
            <MaturityCard />
          </Card>
        </div>

        {/* Main grid — 2-col on desktop, 1-col on mobile */}
        <div style={{
          padding: isMobile ? '0 16px 20px' : '0 32px 28px',
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1.4fr 1fr',
          gap: isMobile ? 20 : 28,
        }}>
          <Card title="drink soon, no later" count="past peak" action="view all" actionTo="/drink-soon">
            <DrinkSoon />
          </Card>
          <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 20 : 28 }}>
            <Card title="the books, by region" count="5 regions" action="detailed" actionTo="/inventory">
              <RegionsCard />
            </Card>
            <Card title="just arrived" count="30 days" action="all" actionTo="/inventory">
              <JustArrived />
            </Card>
          </div>
        </div>

        {/* Tasks */}
        <div style={{ padding: isMobile ? '0 16px 48px' : '0 32px 48px' }}>
          <Card title="pending · tasks" action="Settings" actionTo="/settings">
            <TasksCard />
          </Card>
        </div>

      </div>
    </div>
  );
}
